import produce from 'immer';
import convertDistractorToFacet from 'services/utils/convertDistractorToFacet';
import convertFacetToDistractor from 'services/utils/convertFacetToDistractor';

type Question = {
  id: string;
  text: string;
  update: (text: string) => void;
  explanation: string;
  updateExplanation: (text: string) => void;
  image: {
    add: (url: string, imageUrl: string) => void;
    delete: () => void;
    id?: string;
    altTag?: string;
    url?: string;
  };
  sound: {
    url?: string;
    add: (url: string, id: string) => void;
    delete: () => void;
  };
};

export type Answer = Question & {
  isCorrect: boolean;
  toggle: () => void;
  facetId?: string;
  delete: () => void;
};

export type QuestionAndAnswer = {
  id: string;
  templateType: 'question_and_answers';
  answerType: 'multi_correct' | 'single_correct';
  toggleAnswerType: () => void;
  title: string;
  add: (text?: string) => void;
  updateQuestion: (text: string) => void;
  question: Question;
  answers: Answer[];
};

export default function question_and_answers({ item, distractors, facets, setItem, itemsService }): QuestionAndAnswer {
  const deleteAnswer = (conceptId: string) => {
    const correctCount = item.facets.length;

    if (!item?.isCorrect || correctCount !== 1) {
      setItem((prevItem) =>
        produce(prevItem, (draft) => {
          draft.answers = draft.answers.filter((a) => a.id !== conceptId);
        })
      );

      if (item?.isCorrect) {
        if (item.facetId) {
          facets.delete({ pathParams: { id: item.facetId } });
        }
      } else {
        distractors.delete({ pathParams: { id: conceptId } });
      }
    }
  };

  const changeIfAnswerCorrect = async (conceptId: string, isCorrect: boolean) => {
    let newId = '';
    const tempId = `placeholder-id-${Date.now()}`;

    const correctLength = facets.liveData().length;

    // we can't even momentarily have no correct answers
    // as it means the question won't be able to be reloaded
    if (correctLength === 1 && isCorrect) {
      return;
    }

    setItem((prevItem) =>
      produce(prevItem, (draft) => {
        draft.answers = draft.answers.map((c) =>
          c.id === conceptId
            ? {
                ...c,
                id: tempId,
                isCorrect: !isCorrect,
              }
            : c
        );
      })
    );

    if (isCorrect) {
      // getting facet directly as the item will not be invalidated on toggle to avoid re-rendering
      const facetToRemove = facets.liveData().find((i) => i.association.id === conceptId);
      const payload = convertFacetToDistractor(facetToRemove);

      const newDistractor = await distractors.create({ data: payload });
      newId = newDistractor.id;
      await facets.delete({ pathParams: { id: facetToRemove.id } });
    } else {
      // getting distractor directly as the item will not be invalidated on toggle to avoid re-rendering
      const distractorToRemove = distractors.liveData().find((d) => d.id === conceptId);
      const payload = convertDistractorToFacet(distractorToRemove);

      const newFacet = await facets.create({ pathParams: { id: item.id }, data: payload });
      newId = newFacet.association.id;
      await distractors.delete({ pathParams: { id: distractorToRemove.id } });
    }

    setItem((prevItem) =>
      produce(prevItem, (draft) => {
        draft.answers = draft.answers.map((c) =>
          c.id === tempId
            ? {
                ...c,
                id: newId,
                isCorrect: !isCorrect,
                toggle: () => changeIfAnswerCorrect(newId, !isCorrect),
                updateExplanation: (html) => updateExplanation(newId, html),
                delete: () => deleteAnswer(newId),
                image: {
                  ...c.image,
                  add: (url, imageId) => addImage(newId, imageId, url),
                  delete: () => deleteImage(newId),
                },
              }
            : c
        );
      })
    );
  };

  const updateText = async (conceptId, html) => {
    setItem((prevItem) =>
      produce(prevItem, (draft) => {
        const answer = draft.answers.find((a) => a.id === conceptId);
        if (answer) {
          answer.text = html;
        }
      })
    );

    await distractors.debouncedUpdate({
      pathParams: { id: conceptId },
      data: { data: { attributes: { 'text-html': String(html) } } },
      debounceKey: `q&a_text_${conceptId}`,
    });
  };

  const updateExplanation = (conceptId, html) => {
    setItem((prevItem) =>
      produce(prevItem, (draft) => {
        const answer = draft.answers.find((a) => a.id === conceptId);
        if (answer) {
          answer.explanation = html;
        }
      })
    );

    distractors.debouncedUpdate({
      pathParams: { id: conceptId },
      data: { data: { attributes: { 'explanation-html': String(html) } } },
      debounceKey: `q&a_explanation_${conceptId}`,
    });
  };

  const addAnswer = async (text?: string) => {
    const tempId = `placeholder-id-${Date.now()}`;

    const newAnswer = emptyItem(tempId);
    // If text is provided, set it on the new answer
    if (text) {
      newAnswer.text = text;
    }

    setItem((prevItem) =>
      produce(prevItem, (draft) => {
        draft.answers = [...draft.answers, newAnswer];
      })
    );

    // If text is provided, include it in the creation payload
    const createPayload = text
      ? {
          data: {
            data: {
              attributes: {
                'text-html': String(text),
              },
            },
          },
        }
      : undefined;

    const a = await distractors.create(createPayload);
    const newId = a.id;
    setItem((prevItem) =>
      produce(prevItem, (draft) => {
        const answer = draft.answers.find((a) => a.id === tempId);
        if (answer) {
          answer.id = newId;
          answer.update = (html) => updateText(newId, html);
          answer.updateExplanation = (html) => updateExplanation(newId, html);
          answer.isCorrect = false;
          answer.toggle = () => changeIfAnswerCorrect(newId, false);
          answer.delete = () => deleteAnswer(newId);
          answer.image.add = (url, imageId) => addImage(newId, imageId, url);
          answer.image.delete = () => deleteImage(newId);
        }
      })
    );
  };

  const addImage = async (id: string, url, imageId: string | null) => {
    const isQuestion = id === item.facets[0].anchor.id;

    isQuestion
      ? setItem((prevItem) =>
          produce(prevItem, (draft) => {
            draft.question.image.url = url;
          })
        )
      : setItem((prevItem) =>
          produce(prevItem, (draft) => {
            const answerIndex = draft.answers.findIndex((a) => a.id === id);
            if (answerIndex > -1) {
              draft.answers[answerIndex].image.url = url;
            }
          })
        );

    await distractors.update({
      pathParams: { id },
      data: { data: { relationships: { image: { data: { id: imageId } } } } },
    });
  };

  const deleteImage = (id: string) => {
    addImage(id, undefined, null);
  };

  const formatSubItem = (i) => {
    return {
      id: i.id,
      text: i.text,
      update: (html: string) => updateText(i.id, html),
      explanation: i.explanation,
      updateExplanation: (html: string) => updateExplanation(i.id, html),
      delete: () => deleteAnswer(i.id),
      image: {
        delete: () => deleteImage(i.id),
        add: (url: string, imageId: string) => addImage(i.id, url, imageId),
        id: i.image?.id,
        altTag: i.image?.altTag,
        url: i.image?.smallUrl || i.image?.url,
      },
    };
  };

  function emptyItem(tempId) {
    return formatSubItem({
      id: tempId,
      text: '',
      explanation: '',
      image: null,
    });
  }

  function toggleAnswerType() {
    setItem((prevItem) =>
      produce(prevItem, (draft) => {
        const newAnswerType = prevItem.answerType === 'multi_correct' ? 'single_correct' : 'multi_correct';

        itemsService.update({
          pathParams: { id: item.id },
          data: { data: { attributes: { 'answer-type': newAnswerType } } },
        });

        draft.answerType = newAnswerType;
      })
    );
  }

  const updateQuestion = async (questionText) => {
    setItem((prevItem) =>
      produce(prevItem, (draft) => {
        draft.title = questionText;
        draft.question.text = questionText;
      })
    );
    await distractors.debouncedUpdate({
      pathParams: { id: item.facets[0].anchor.id },
      data: { data: { attributes: { 'text-html': String(questionText) } } },
      debounceKey: 'q&a_title',
    });
  };

  const addSound = (url: string | undefined, id: string | undefined) => {
    distractors.update({
      pathParams: { id: item.facets[0].anchor.id },
      data: {
        data: { relationships: { sound: { data: { id } } } },
      },
    });

    setItem((prev) =>
      produce(prev, (draft) => {
        draft.question.sound.url = url;
      })
    );
  };

  const deleteSound = () => {
    addSound(undefined, undefined);
  };

  return {
    id: item.id,
    templateType: item.templateType,
    title: item.title || item.facets[0].anchor.text || 'Untitled',
    question: {
      ...formatSubItem(item.facets[0].anchor),
      sound: {
        url: item.facets[0].anchor.sound?.id,
        add: addSound,
        delete: deleteSound,
      },
    } as Question,
    updateQuestion,
    answerType: item.answerType,
    toggleAnswerType,
    add: addAnswer,
    answers: [
      ...facets.data.map((f) => ({
        facetId: f.id,
        ...formatSubItem(f.association),
        toggle: () => changeIfAnswerCorrect(f.association.id, true),
        isCorrect: true,
      })),
      ...distractors.data.map((d) => ({
        ...formatSubItem(d),
        toggle: () => changeIfAnswerCorrect(d.id, false),
        isCorrect: false,
      })),
    ],
  };
}
